import React, { useState } from 'react';
import { Alert, Form, Card, Input, Button, message, Radio, Select } from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
// import { QuestionCircleOutlined } from '@ant-design/icons';
import Error from '../../../components/Error';
import Loading from '../../../components/Loading';
import PageTitle from '../../../components/PageTitle';
import ProgressModal from '../../../components/ProgressModal';
import { ORDER_COUNTRIES, SHIPMENT_TYPE } from '../../../utils/constants';
import { DIRECT_ORDER_SELECT_SERVICE_ENABLED } from '../../../config';
import { filterShippingRoutesForDirect, findShippingRoute, priceRangeOfShippingRoute, shortPriceDescription, useShippingRoutes } from '../../../utils/shippingRouteHelper';
import { ShipmentType } from '../../../utils/types';

const ADD_ORDER_MUTATION = gql`
  mutation AddOrderMutation($order: OrderInput!) {
    addOrder(order: $order) {
      id
    }
  }
`;

export type PlaceOrderState = {
  packageIds: string[],
  totalWeight: number,
}

type FormType = {
  recipientName: string,
  phone: string,
  country: string,
  type: ShipmentType,
  shippingService: string,
  address: string,
  city: string,
  state: string,
  zipCode: string,
  notes: string,
};

function PlaceOrder() {
  const location = useLocation<PlaceOrderState>();
  const onFinish = (values: FormType) => {
    if (!location?.state?.packageIds) {
      message.error('无效订单，请先勾选包裹');
      history.replace('/packages');
      return;
    }
    const input = {
      recipientName: values.recipientName,
      phone: values.phone,
      country: values.country,
      ...(values.shippingService ? { shippingService: values.shippingService } : {}),
      address: values.address,
      city: values.city,
      state: values.state,
      zipCode: values.zipCode,
      notes: values.notes,
      packageIds: location?.state?.packageIds,
    };
    ProgressModal.show();
    addOrder({ variables: { order: input } });
  };

  const [addOrder] = useMutation(ADD_ORDER_MUTATION, {
    onError: () => null,
    onCompleted: data => {
      console.log(data);
      ProgressModal.hide();
      message.success('下单成功，已提交仓库打包，有任何问题请联系客服');
      history.replace('/orders');
    },
  });

  const history = useHistory();

  if (!location?.state?.packageIds) {
    message.error('下单前请先勾选包裹');
    history.replace('/packages');
  }

  const totalWeight = location?.state?.totalWeight;

  const [form] = Form.useForm<FormType>();

  const { loading: loadingShippingRoutes, data: shippingRoutesData = { shippingRoutes: null } } = useShippingRoutes();

  const [serviceNotes, setServiceNotes] = useState('');

  const resetShippingService = () => {
    form.setFieldsValue({ shippingService: '' });
    setServiceNotes('');
  };

  if (loadingShippingRoutes) {
    return <Loading />
  }

  const { shippingRoutes } = shippingRoutesData;

  if (!shippingRoutes) {
    return <Error />
  }

  const changeShippingService = value => {
    const route = findShippingRoute(shippingRoutes, value);
    setServiceNotes(route?.notes || '');
  };

  return (
    <div className="PlaceOrder">
      <PageTitle>创建订单</PageTitle>
      <Card bordered={false}>
        <Form onFinish={onFinish} initialValues={{ type: 'REGULAR' }} form={form}>
          <Form.Item
            label="收件人名字"
            name="recipientName"
            rules={[{ required: true, message: '必填' }]}
          >
            <Input allowClear />
          </Form.Item>
          <Form.Item label="联系电话" name="phone" rules={[{ required: true, message: '必填' }]}>
            <Input allowClear />
          </Form.Item>
          <Form.Item label="目的国家" name="country" rules={[{ required: true, message: '必填' }]}>
            <Select onChange={resetShippingService} >
              {Object.entries(ORDER_COUNTRIES).map(([countryCode, countryName]) => (
                <Select.Option value={countryCode} key={countryCode}>
                  {countryName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {DIRECT_ORDER_SELECT_SERVICE_ENABLED && (
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                return (
                  filterShippingRoutesForDirect(shippingRoutes, getFieldValue('country')).length > 0 &&
                  <>
                    <Form.Item label="线路类型" name="type" rules={[{ required: true, message: '必填' }]}>
                      <Radio.Group onChange={resetShippingService}>
                        {Object.entries(SHIPMENT_TYPE).filter(([type]) => type !== ShipmentType.Both).map(([type, value]) =>
                          <Radio value={type} key={type}>{value.text}</Radio>
                        )}
                      </Radio.Group>
                    </Form.Item>
                    <Form.Item
                      label="线路渠道"
                      name="shippingService"
                      rules={[{ required: true, message: '必填' }]}
                    >
                      <Select onChange={changeShippingService}>
                        {filterShippingRoutesForDirect(shippingRoutes, getFieldValue('country'), getFieldValue('type'))
                          .map((route, index) => (
                            <Select.Option value={route.id} key={index}>
                              {route.name} {((priceRange) => {
                                const minWeight = route.priceRanges[0].startWeight;
                                let output = '';
                                if (minWeight > 0) {
                                  output += `${minWeight}kg起 `
                                }
                                if (route.priceRanges.length > 1) {
                                  // If there are multiple price ranges, then the price is an estimate
                                  output += '~';
                                }
                                output += shortPriceDescription(priceRange);
                                return output;
                              })(priceRangeOfShippingRoute(route, totalWeight))}
                              &nbsp;{route.speed}
                            </Select.Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </>
                );
              }}
            </Form.Item>
          )}

          {serviceNotes && <Alert type="warning" message={serviceNotes} />}

          <Form.Item label="街道地址" name="address" rules={[{ required: true, message: '必填' }]}>
            <Input allowClear />
          </Form.Item>

          <Form.Item label="城市" name="city" rules={[{ required: true, message: '必填' }]}>
            <Input allowClear />
          </Form.Item>

          <Form.Item label="州/省" name="state" rules={[{ required: true, message: '必填' }]}>
            <Input allowClear />
          </Form.Item>

          <Form.Item
            label="邮编"
            name="zipCode"
            rules={[
              { required: true, message: '必填' },
              // { type: 'string', pattern: /^[0-9]+$/, len: 5, message: '邮编不合法'},
            ]}
          >
            <Input allowClear />
          </Form.Item>

          <Form.Item
            label="备注"
            name="notes"
          >
            <Input.TextArea
              allowClear
              placeholder={DIRECT_ORDER_SELECT_SERVICE_ENABLED ? '' : '请备注线路，转寄国内的话是否指定快递'}
            />
          </Form.Item>

          <Form.Item>
            <Button block type="primary" htmlType="submit">
              确认下单
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

export default PlaceOrder;
